import React, { useEffect, useRef, useState } from 'react'
import Button from '../../../../../components/ui/buttons/Button'
import Input from '../../../../../components/ui/inputs/Input'
import CustomInput from '../../../../../components/ui/inputs/CustomInput'
import { fetchWithCsrf } from '../../../../../utils/fetchWithCsrf'
import { snakecaseKeys } from '../../../../../utils/snakecaseKeys'
import { camelizeKeys } from '../../../../../utils/camelizeKeys'
import Checkbox from '../../../../../components/ui/checkboxes/CustomCheckbox'
import { useToast } from '../../../../../components/ui/toast/ToastContext'
import { useConfirm } from '../../../../../components/ui/confirm/ConfirmContext'

const SpouseTab = ({ clientId, onSuccess, canWrite }) => {
  const [form, setForm] = useState({ spouse: null, hasntSpouse: false })
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedSpouse, setSelectedSpouse] = useState(null)
  const [saving, setSaving] = useState(false)
  const debounceRef = useRef(null)
  const { addToast } = useToast()
  const confirm = useConfirm()

  const fetchSpouse = () => {
    fetch(`/app/planner/client/${clientId}/profile/spouse`)
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)
        setForm({
          spouse: camelData.spouse || null,
          hasntSpouse: camelData.hasntSpouse || false
        })
        setSelectedSpouse(camelData.spouse || null)
      })
  }

  useEffect(() => {
    fetchSpouse()
  }, [clientId])

  useEffect(() => {
    if (searchTerm.length < 3) return setSearchResults([])

    const controller = new AbortController()
    fetch(`/app/planner/client/${clientId}/profile/spouse/search?q=${encodeURIComponent(searchTerm)}`, { signal: controller.signal })
      .then(res => res.json())
      .then(data => setSearchResults(camelizeKeys(data)))
      .catch(err => console.warn('Busca cancelada ou erro:', err))

    return () => controller.abort()
  }, [searchTerm])

  const handleSubmit = () => {
    setSaving(true)

    const payload = {
      client: {
        spouse_id: selectedSpouse?.id || null,
        hasnt_spouse: form.hasntSpouse
      }
    }

    fetchWithCsrf(`/app/planner/client/${clientId}/profile/spouse`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(() => {
        addToast({ type: 'success', message: 'Cônjuge vinculado com sucesso!' })
        fetchSpouse()
        onSuccess()
      })
      .catch(() => addToast({ type: 'error', message: 'Erro ao salvar vínculo do cônjuge.' }))
      .finally(() => setSaving(false))
  }

  const handleUnlink = async () => {
    const ok = await confirm({
      title: 'DESVINCULAR CÔNJUGE?',
      description: `Tem certeza que deseja desvincular o cônjuge atual?`,
      confirmText: 'DESVINCULAR'
    })

    if (!ok) return

    fetchWithCsrf(`/app/planner/client/${clientId}/profile/spouse`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client: { spouse_id: null } })
    })
      .then(() => {
        addToast({ type: 'success', message: 'Cônjuge desvinculado com sucesso!' })
        fetchSpouse()
        onSuccess()
      })
      .catch(() => addToast({ type: 'error', message: 'Erro ao desvincular cônjuge.' }))
  }

  const updateHasntSpouse = (newValue) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      fetchWithCsrf(`/app/planner/client/${clientId}/profile/spouse`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client: { hasnt_spouse: newValue } })
      })
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(() => {
          addToast({ type: 'success', message: 'Atualizado com sucesso.' })
          onSuccess()
        })
        .catch(() => {
          addToast({ type: 'error', message: 'Erro ao atualizar informação de cônjuge.' })
        })
    }, 800)
  }

  return (
    <div className="bg-white rounded-xl p-6 space-y-4">
      <h2 className="text-lg font-semibold text-blueDarker">
        Adicione a conta do cônjuge para um acompanhamento mais preciso
      </h2>
      <div>
        <h3 className="text-base text-blueDarker">
          Busque pelo nome na nossa base de clientes. Caso não possua cônjuge ou seu cônjuge ainda não esteja cadastrado, pule esta etapa.
        </h3>
        <h3 className="text-base text-blueDarker">
          O vínculo de contas poderá ser realizado posteriormente, quando for cadastrá-lo.
        </h3>
      </div>

      {form.spouse ? (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 flex flex-col items-center">
          <p className="mb-2 font-medium text-blueDarker">Cônjuge vinculado:</p>
          <p><strong>Nome:</strong> {form.spouse.name}</p>
          <p><strong>CPF:</strong> {form.spouse.cpfCnpj}</p>
          <p><strong>Data de nascimento:</strong> {form.spouse.birthDate}</p>

          {canWrite && (
            <div className="mt-4">
              <Button onClick={handleUnlink} type="button" className="bg-red-500 border-red-700">
                DESVINCULAR CÔNJUGE
              </Button>
            </div>
          )}
        </div>
      ) : canWrite ? (
        <>
          {!form.hasntSpouse && (
            <div className="space-y-4">
              <div className="flex gap-6">
                <CustomInput
                  name="search"
                  placeholder="Pesquisar Cliente..."
                  inputClassName="w-custom500"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
                <Button onClick={handleSubmit} loading={saving}>
                  VINCULAR CONTAS
                </Button>
              </div>

              {searchResults.length > 0 && (
                <ul className="border border-gray-300 rounded-md bg-white divide-y">
                  {searchResults.map(client => (
                    <li
                      key={client.id}
                      className="px-4 py-2 hover:bg-tealWhisper cursor-pointer"
                      onClick={() => {
                        setSelectedSpouse(client)
                        setSearchTerm(`${client.name} - ${client.cpfCnpj}`)
                        setSearchResults([])
                      }}
                    >
                      {client.name} - {client.cpfCnpj}
                    </li>
                  ))}
                </ul>
              )}

              {selectedSpouse && (
                <div className="bg-tealLight/10 p-4 rounded border border-tealBase">
                  <p><strong>Selecionado:</strong> {selectedSpouse.name} - {selectedSpouse.cpfCnpj}</p>
                </div>
              )}
            </div>
          )}

          {!form.spouse && (
            <Checkbox
              name="hasntSpouse"
              label="Não possuo cônjuge ou ele ainda não está cadastrado"
              labelClassName="text-base"
              checked={form.hasntSpouse}
              onChange={() => {
                setForm(prev => {
                  const newValue = !prev.hasntSpouse
                  updateHasntSpouse(newValue)
                  return { ...prev, hasntSpouse: newValue }
                })
              }}
            />
          )}
        </>
      ) : (
        <p className="text-base text-blueSteel italic">
          Nenhum cônjuge vinculado. A edição não está disponível com o nível de acesso atual.
        </p>
      )}
    </div>
  )
}

export default SpouseTab
